/**
 * site-scope-picker.js — Site scope / EE / vault picker UI
 *
 * Owns the portal-based site-scope picker dropdown used for:
 *   - existing-environment field selection
 *   - VDC Vault source storage, repository, and target vault pickers
 *
 * Extracted from script.js (was lines 979–1529).
 * Also owns getExistingEnvironmentPickerOptions (was line 1671) and the
 * vdcVault* option arrays (were lines 6531–6551 in script.js).
 *
 * Cross-dependency: openSiteScopePicker calls window.closeWorkloadPicker (set by
 * workload-picker.js). window bridge avoids circular-import init-order issues.
 */

import { siteScopePickerUiState, workloadPickerUiState, sites, existingEnvironmentFieldLabels } from './state.js';
import {
    buildSiteScopePickerKey, parseSiteScopePickerKey,
    getSiteScopePickerTriggerSelector, getSiteScopePickerSearchSelector,
    isSiteScopePickerOpen, getSiteScopePickerSearchQuery, setSiteScopePickerSearchQuery,
    clearSiteScopePickerPortal, isActiveElementWithinSiteScopePicker,
    getRepoMigrationPickerOptionLabel, getRepoMigrationWorkloads,
    getSiteById, getSiteIndexById, normalizeWorkloadPickerSearchQuery,
} from './helpers.js';
import { getSiteWorkloadByIdentifier, getWorkloadRowId } from './palette.js';
import { escapeHtml } from './utils.js';
import { getSiteDisplayName, getExistingEnvironmentDisplayItems,
         getExistingEnvironmentSelectableFieldOrder, getAvailableExistingEnvironmentFields,
         createExistingEnvironmentSelectionState } from './recalc.js';
import { positionWorkloadPickerPortalPanel } from './workload-picker.js';

// ── Portal host ────────────────────────────────────────────────────────────────

const siteScopePickerPortalHost = document.createElement('div');
window.siteScopePickerPortalHost = siteScopePickerPortalHost;
siteScopePickerPortalHost.className = 'workload-picker-portal-host';
siteScopePickerPortalHost.hidden = true;
document.body?.appendChild(siteScopePickerPortalHost);

// ── Module state ───────────────────────────────────────────────────────────────

export const existingEnvironmentPickerPlaceholderLabel = 'Select Veeam Component...';
export const pendingExistingEnvironmentSelectionKey = '__pending_existing_environment__';
let siteScopePickerRepositionFrame = 0;
window.siteScopePickerRepositionFrame = siteScopePickerRepositionFrame;

// ── Vault option arrays (exported — script.js imports these) ──────────────────

export const repoMigrationSourceStorageOptions = [
    { value: '', label: 'Select source storage type' },
    { value: 'azure-blob', label: 'Azure BLOB' },
    { value: 'aws-s3', label: 'AWS S3' },
    { value: 'onprem-s3', label: 'S3-Compatible (on-premises)' },
    { value: 'smb-nfs', label: 'SMB/NFS' },
    { value: 'cloud-s3', label: 'Cloud S3-compatible (Wasabi, Backblaze, etc.)' },
    { value: 'onprem-block', label: 'On-premises block' },
    { value: 'dedup-appliance', label: 'Deduplication Appliance' }
];

export const repoMigrationTargetOptions = [
    { value: '', label: 'Select migration target' },
    { value: 'block-performance-tier', label: 'Block - Performance Tier' },
    { value: 'blob-performance-tier',  label: 'BLOB - Performance Tier' },
    { value: 'vdc-vault-aws',          label: 'VDC Vault AWS' },
    { value: 'vdc-vault-azure',        label: 'VDC Vault Azure' }
];

export const repoMigrationRepositoryOptions = [
    { value: '', label: 'Select repository type' },
    { value: 'sobr-performance-tier', label: 'SOBR - Performance Tier' },
    { value: 'sobr-capacity-tier', label: 'SOBR Capacity Tier' },
    { value: 'stand-alone', label: 'Stand-alone' }
];

// ── Picker key builders ────────────────────────────────────────────────────────

export function getExistingEnvironmentSiteScopePickerKey(siteId, field) {
    return buildSiteScopePickerKey('existing-environment', siteId, field);
}

export function getRepoMigrationSourceStoragePickerKey(siteId, workloadIdentifier) {
    return buildSiteScopePickerKey('repo-migration-source', siteId, workloadIdentifier);
}

export function getRepoMigrationRepositoryPickerKey(siteId, workloadIdentifier) {
    return buildSiteScopePickerKey('repo-migration-repository', siteId, workloadIdentifier);
}

export function getRepoMigrationTargetPickerKey(siteId, workloadIdentifier) {
    return buildSiteScopePickerKey('repo-migration-target', siteId, workloadIdentifier);
}

// ── EE picker options (was getExistingEnvironmentPickerOptions in script.js) ──

export function getExistingEnvironmentPickerOptions(site, currentField = '') {
    const isPendingSelection = currentField === pendingExistingEnvironmentSelectionKey;
    const selectedField = isPendingSelection ? '' : currentField;
    const selectionState = createExistingEnvironmentSelectionState(site?.existingEnvironment, selectedField);
    const selectableFields = getExistingEnvironmentSelectableFieldOrder(selectionState);
    const availableFields = new Set(getAvailableExistingEnvironmentFields(selectionState));

    return selectableFields
        .filter((key) => key === selectedField || availableFields.has(key))
        .map((key) => ({
            value: key,
            label: existingEnvironmentFieldLabels[key] || key
        }));
}

// ── Descriptor builder ─────────────────────────────────────────────────────────

function getSiteScopePickerDescriptorByKey(key) {
    const parsedKey = parseSiteScopePickerKey(key);
    if (!parsedKey) {
        return null;
    }

    const { pickerType, siteId, itemId } = parsedKey;
    const site = getSiteById(siteId);
    if (!site) {
        return null;
    }

    if (pickerType === 'existing-environment') {
        const currentField = itemId;
        const selectedValue = currentField === pendingExistingEnvironmentSelectionKey ? '' : currentField;
        const options = getExistingEnvironmentPickerOptions(site, currentField);
        const selectedLabel = selectedValue ? (existingEnvironmentFieldLabels[selectedValue] || selectedValue) : existingEnvironmentPickerPlaceholderLabel;
        const pickerId = `site-scope-picker-existing-environment-${site.id}-${currentField}`;

        return {
            key,
            pickerType,
            site,
            siteId,
            itemId: currentField,
            pickerId,
            panelId: `${pickerId}-panel`,
            searchInputId: `${pickerId}-search`,
            options,
            selectedValue,
            selectedLabel,
            placeholderLabel: existingEnvironmentPickerPlaceholderLabel,
            ariaLabel: `Veeam component or job for ${getSiteDisplayName(site, getSiteIndexById(site.id))}`
        };
    }

    const workload = getSiteWorkloadByIdentifier(site, itemId);
    if (!workload) {
        return null;
    }

    const pickerIdPrefix = pickerType.replace(/[^a-z0-9-]+/gi, '-');
    const pickerId = `site-scope-picker-${pickerIdPrefix}-${site.id}-${itemId}`;
    const siteDisplayName = getSiteDisplayName(site, getSiteIndexById(site.id));

    if (pickerType === 'repo-migration-source') {
        const selectedValue = workload.vdcVaultSourceStorageType || '';

        return {
            key,
            pickerType,
            site,
            siteId,
            itemId,
            pickerId,
            panelId: `${pickerId}-panel`,
            searchInputId: `${pickerId}-search`,
            options: repoMigrationSourceStorageOptions,
            selectedValue,
            selectedLabel: getRepoMigrationPickerOptionLabel(repoMigrationSourceStorageOptions, selectedValue) || repoMigrationSourceStorageOptions[0].label,
            placeholderLabel: repoMigrationSourceStorageOptions[0].label,
            ariaLabel: `Current state storage for ${siteDisplayName}`
        };
    }

    if (pickerType === 'repo-migration-repository') {
        const selectedValue = workload.vdcVaultRepositoryType || '';

        return {
            key,
            pickerType,
            site,
            siteId,
            itemId,
            pickerId,
            panelId: `${pickerId}-panel`,
            searchInputId: `${pickerId}-search`,
            options: repoMigrationRepositoryOptions,
            selectedValue,
            selectedLabel: getRepoMigrationPickerOptionLabel(repoMigrationRepositoryOptions, selectedValue) || repoMigrationRepositoryOptions[0].label,
            placeholderLabel: repoMigrationRepositoryOptions[0].label,
            ariaLabel: `Repository for ${siteDisplayName}`
        };
    }

    if (pickerType === 'repo-migration-target') {
        const selectedValue = workload.vdcVaultTargetVault || '';

        return {
            key,
            pickerType,
            site,
            siteId,
            itemId,
            pickerId,
            panelId: `${pickerId}-panel`,
            searchInputId: `${pickerId}-search`,
            options: repoMigrationTargetOptions,
            selectedValue,
            selectedLabel: getRepoMigrationPickerOptionLabel(repoMigrationTargetOptions, selectedValue) || repoMigrationTargetOptions[0].label,
            placeholderLabel: repoMigrationTargetOptions[0].label,
            ariaLabel: `Target vault for ${siteDisplayName}`
        };
    }

    return null;
}

function getOpenSiteScopePickerDescriptor() {
    return getSiteScopePickerDescriptorByKey(siteScopePickerUiState.openKey);
}

// ── Markup builder ─────────────────────────────────────────────────────────────

function buildSiteScopePickerPanelMarkup(descriptor) {
    const isOpen = isSiteScopePickerOpen(descriptor.key);
    const searchQuery = getSiteScopePickerSearchQuery(descriptor.key);
    const menuOptions = descriptor.pickerType.startsWith('repo-migration-')
        ? descriptor.options.filter((option) => option.value)
        : descriptor.options;
    const optionsMarkup = menuOptions.map((option) => {
        const isSelected = option.value === descriptor.selectedValue;
        const optionMetaMarkup = isSelected && option.value
            ? '<span class="site-workload-picker__option-meta"><span class="site-workload-picker__option-badge">Selected</span></span>'
            : '';

        return `
            <button
                type="button"
                class="site-workload-picker__option${isSelected ? ' is-selected' : ''}"
                data-site-scope-picker-key="${escapeHtml(descriptor.key)}"
                data-site-scope-picker-next-value="${escapeHtml(option.value)}"
                data-site-scope-picker-option-label="${escapeHtml(option.label.toLowerCase())}"
                aria-pressed="${String(isSelected)}"
            >
                <span class="site-workload-picker__option-label">${escapeHtml(option.label)}</span>
                ${optionMetaMarkup}
            </button>
        `;
    }).join('');

    return `
        <div
            id="${descriptor.panelId}"
            class="site-workload-picker__panel site-workload-picker__panel--portal"
            data-site-scope-picker-key="${escapeHtml(descriptor.key)}"
            aria-labelledby="${descriptor.pickerId}"
            ${isOpen ? '' : 'hidden'}
        >
            <div class="site-workload-picker__search-shell">
                <label class="sr-only" for="${descriptor.searchInputId}">Search</label>
                <div class="site-workload-picker__search-control">
                    <span class="site-workload-picker__search-icon" aria-hidden="true">
                        <svg viewBox="0 0 16 16" focusable="false">
                            <circle cx="7" cy="7" r="4.5"></circle>
                            <path d="M10.5 10.5 14 14"></path>
                        </svg>
                    </span>
                    <input
                        type="search"
                        id="${descriptor.searchInputId}"
                        class="site-workload-picker__search"
                        value="${escapeHtml(searchQuery)}"
                        placeholder="Search"
                        autocomplete="off"
                        spellcheck="false"
                        data-site-scope-picker-search="${escapeHtml(descriptor.key)}"
                    />
                </div>
            </div>
            <div class="site-workload-picker__categories">
                <section class="site-workload-picker__category" data-site-scope-picker-category-shell="${escapeHtml(descriptor.key)}">
                    <div class="site-workload-picker__category-panel">
                        ${optionsMarkup}
                    </div>
                </section>
                <p class="site-workload-picker__empty" data-site-scope-picker-empty="${escapeHtml(descriptor.key)}" ${menuOptions.length ? 'hidden' : ''}>No results found</p>
            </div>
        </div>
    `;
}

// ── State sync ─────────────────────────────────────────────────────────────────

function syncSiteScopePickerRootState(root) {
    if (!(root instanceof HTMLElement)) {
        return;
    }

    const key = root.dataset.siteScopePickerRoot || '';
    if (!key) {
        return;
    }

    const isOpen = isSiteScopePickerOpen(key);
    const trigger = root.querySelector(getSiteScopePickerTriggerSelector(key));

    root.classList.toggle('is-open', isOpen);

    if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', String(isOpen));
    }
}

export function syncSiteScopePickerPanelState(container, key) {
    if (!container || typeof container.querySelector !== 'function') {
        return;
    }

    const descriptor = getSiteScopePickerDescriptorByKey(key);
    if (!descriptor) {
        return;
    }

    const panel = container.querySelector(`[data-site-scope-picker-key="${escapeHtml(key)}"]`);
    if (!(panel instanceof HTMLElement)) {
        return;
    }

    const isOpen = isSiteScopePickerOpen(key);
    const query = getSiteScopePickerSearchQuery(key);
    const normalizedQuery = normalizeWorkloadPickerSearchQuery(query);
    const searchInput = panel.querySelector(getSiteScopePickerSearchSelector(key));
    const category = panel.querySelector('[data-site-scope-picker-category-shell]');
    let visibleOptionTotal = 0;

    panel.hidden = !isOpen;

    if (searchInput instanceof HTMLInputElement && searchInput.value !== query) {
        searchInput.value = query;
    }

    panel.querySelectorAll('[data-site-scope-picker-next-value]').forEach((optionButton) => {
        if (!(optionButton instanceof HTMLElement)) {
            return;
        }

        const optionLabel = optionButton.dataset.siteScopePickerOptionLabel || '';
        const matchesQuery = !normalizedQuery || optionLabel.includes(normalizedQuery);
        optionButton.hidden = !matchesQuery;

        if (matchesQuery) {
            visibleOptionTotal += 1;
        }
    });

    if (category instanceof HTMLElement) {
        category.hidden = visibleOptionTotal === 0;
    }

    const emptyState = panel.querySelector(`[data-site-scope-picker-empty="${escapeHtml(key)}"]`);
    if (emptyState instanceof HTMLElement) {
        emptyState.hidden = visibleOptionTotal > 0;
    }
}

function positionOpenSiteScopePickerPanel() {
    const descriptor = getOpenSiteScopePickerDescriptor();
    if (!descriptor) {
        return;
    }

    const siteList = document.getElementById('site-list');
    const trigger = siteList.querySelector(getSiteScopePickerTriggerSelector(descriptor.key));
    const panel = siteScopePickerPortalHost.querySelector(`[data-site-scope-picker-key="${escapeHtml(descriptor.key)}"]`);

    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
        return;
    }

    positionWorkloadPickerPortalPanel(panel, trigger);
}

export function scheduleOpenSiteScopePickerPanelPosition() {
    if (!siteScopePickerUiState.openKey || siteScopePickerRepositionFrame) {
        return;
    }

    siteScopePickerRepositionFrame = window.requestAnimationFrame(() => {
        siteScopePickerRepositionFrame = 0;
        window.siteScopePickerRepositionFrame = siteScopePickerRepositionFrame;
        positionOpenSiteScopePickerPanel();
    });
    window.siteScopePickerRepositionFrame = siteScopePickerRepositionFrame;
}

function renderSiteScopePickerPortal() {
    const descriptor = getOpenSiteScopePickerDescriptor();
    if (!descriptor) {
        clearSiteScopePickerPortal();
        return;
    }

    const siteList = document.getElementById('site-list');
    const trigger = siteList.querySelector(getSiteScopePickerTriggerSelector(descriptor.key));
    if (!(trigger instanceof HTMLElement)) {
        clearSiteScopePickerPortal();
        return;
    }

    siteScopePickerPortalHost.hidden = false;
    siteScopePickerPortalHost.innerHTML = buildSiteScopePickerPanelMarkup(descriptor);

    const panel = siteScopePickerPortalHost.querySelector(`[data-site-scope-picker-key="${escapeHtml(descriptor.key)}"]`);
    if (!(panel instanceof HTMLElement)) {
        clearSiteScopePickerPortal();
        return;
    }

    syncSiteScopePickerPanelState(siteScopePickerPortalHost, descriptor.key);
    positionWorkloadPickerPortalPanel(panel, trigger);
}

export function syncAllSiteScopePickerStates() {
    const siteList = document.getElementById('site-list');
    siteList.querySelectorAll('[data-site-scope-picker-root]').forEach((root) => {
        syncSiteScopePickerRootState(root);
    });

    renderSiteScopePickerPortal();
}

// ── Open / close ───────────────────────────────────────────────────────────────

export function openSiteScopePicker(key) {
    const descriptor = getSiteScopePickerDescriptorByKey(key);
    if (!descriptor) {
        return;
    }

    // Close the workload picker if open — use window bridge to avoid
    // circular import init-order issues (workload-picker.js sets this).
    if (workloadPickerUiState.openKey) {
        window.closeWorkloadPicker?.();
    }

    siteScopePickerUiState.openKey = key;
    syncAllSiteScopePickerStates();

    const searchInput = siteScopePickerPortalHost.querySelector(getSiteScopePickerSearchSelector(key));
    if (searchInput instanceof HTMLInputElement) {
        searchInput.focus();
        searchInput.select();
    }
}

export function closeSiteScopePicker(key = '') {
    if (!key || siteScopePickerUiState.openKey === key) {
        siteScopePickerUiState.openKey = '';
    }

    syncAllSiteScopePickerStates();
}

// window bridge so workload-picker.js can call this without a circular import
window.closeSiteScopePicker = closeSiteScopePicker;

// ── Valid keys / pruning ───────────────────────────────────────────────────────

function getValidSiteScopePickerKeys() {
    const validKeys = [];

    // window bridges for functions still in script.js
    if (window.shouldRenderExistingEnvironmentTable?.()) {
        sites.forEach((site) => {
            getExistingEnvironmentDisplayItems(site.existingEnvironment, site.existingEnvironmentCustomLabels, site.existingEnvironmentDisplayOrder)
                .forEach((item) => {
                    validKeys.push(getExistingEnvironmentSiteScopePickerKey(site.id, item.key));
                });
        });
    }

    if (window.shouldRenderRepoMigrationTable?.()) {
        sites.forEach((site) => {
            getRepoMigrationWorkloads(site).forEach((workload) => {
                const workloadRowId = getWorkloadRowId(workload);
                validKeys.push(getRepoMigrationSourceStoragePickerKey(site.id, workloadRowId));
                validKeys.push(getRepoMigrationRepositoryPickerKey(site.id, workloadRowId));
                validKeys.push(getRepoMigrationTargetPickerKey(site.id, workloadRowId));
            });
        });
    }

    return validKeys;
}

export function pruneSiteScopePickerUiState() {
    const validKeys = new Set(getValidSiteScopePickerKeys());

    Object.keys(siteScopePickerUiState.searchQueryByKey).forEach((key) => {
        if (!validKeys.has(key)) {
            delete siteScopePickerUiState.searchQueryByKey[key];
        }
    });

    if (siteScopePickerUiState.openKey && !validKeys.has(siteScopePickerUiState.openKey)) {
        siteScopePickerUiState.openKey = '';
    }
}

// ── VDC Vault picker value update ──────────────────────────────────────────────

function updateRepoMigrationSiteScopePickerValue(siteId, workloadIdentifier, property, nextValue, key) {
    const site = getSiteById(siteId);
    const workload = getSiteWorkloadByIdentifier(site, workloadIdentifier);
    if (!workload) {
        return false;
    }

    workload[property] = String(nextValue || '');
    // window bridge — renderSiteCard is still in script.js
    window.renderSiteCard?.(siteId, getSiteScopePickerTriggerSelector(key));
    return true;
}

// ── Event handlers (delegated from portal host and site list) ──────────────────

export function handleSiteScopePickerClick(event) {
    if (!(event.target instanceof Element)) {
        return false;
    }

    const optionButton = event.target.closest('[data-site-scope-picker-next-value]');
    if (optionButton) {
        const key = optionButton.dataset.siteScopePickerKey || '';
        const nextValue = optionButton.dataset.siteScopePickerNextValue;
        const descriptor = getSiteScopePickerDescriptorByKey(key);

        if (!descriptor || nextValue === undefined) {
            return true;
        }

        if (descriptor.selectedValue === nextValue) {
            closeSiteScopePicker(key);
            const siteList = document.getElementById('site-list');
            siteList.querySelector(getSiteScopePickerTriggerSelector(key))?.focus();
            return true;
        }

        if (descriptor.pickerType === 'existing-environment') {
            // window bridge — updateSiteExistingEnvironmentValue is still in script.js
            window.updateSiteExistingEnvironmentValue?.(descriptor.siteId, descriptor.itemId, nextValue);
            return true;
        }

        if (descriptor.pickerType === 'repo-migration-source') {
            updateRepoMigrationSiteScopePickerValue(descriptor.siteId, descriptor.itemId, 'vdcVaultSourceStorageType', nextValue, key);
            return true;
        }

        if (descriptor.pickerType === 'repo-migration-repository') {
            updateRepoMigrationSiteScopePickerValue(descriptor.siteId, descriptor.itemId, 'vdcVaultRepositoryType', nextValue, key);
            return true;
        }

        if (descriptor.pickerType === 'repo-migration-target') {
            updateRepoMigrationSiteScopePickerValue(descriptor.siteId, descriptor.itemId, 'vdcVaultTargetVault', nextValue, key);
            return true;
        }

        return true;
    }

    const triggerButton = event.target.closest('[data-site-scope-picker-trigger]');
    if (triggerButton) {
        const key = triggerButton.dataset.siteScopePickerTrigger || '';
        if (!key) {
            return true;
        }

        if (isSiteScopePickerOpen(key)) {
            closeSiteScopePicker(key);
            triggerButton.focus();
            return true;
        }

        openSiteScopePicker(key);
        return true;
    }

    return false;
}

export function handleSiteScopePickerKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const pickerElement = target.closest('[data-site-scope-picker-key]');
    if (pickerElement && event.key === 'Escape') {
        const pickerKey = pickerElement.dataset.siteScopePickerKey || '';

        if (pickerKey) {
            event.preventDefault();
            closeSiteScopePicker(pickerKey);
            const siteList = document.getElementById('site-list');
            siteList.querySelector(getSiteScopePickerTriggerSelector(pickerKey))?.focus();
        }
        return true;
    }

    if (target.matches('[data-site-scope-picker-search]') && event.key === 'Enter') {
        event.preventDefault();
        return true;
    }

    return false;
}

export function handleSiteScopePickerInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('[data-site-scope-picker-search]')) {
        return false;
    }

    const input = target;
    const key = input.dataset.siteScopePickerSearch || '';

    if (!key) {
        return true;
    }

    setSiteScopePickerSearchQuery(key, input.value);
    syncSiteScopePickerPanelState(siteScopePickerPortalHost, key);
    scheduleOpenSiteScopePickerPanelPosition();
    return true;
}

export function handleSiteScopePickerFocusOut(event) {
    const target = event.target;

    if (!(target instanceof HTMLElement)) {
        return;
    }

    const pickerElement = target.closest('[data-site-scope-picker-key]');
    if (!pickerElement) {
        return;
    }

    const pickerKey = pickerElement.dataset.siteScopePickerKey || '';
    window.setTimeout(() => {
        if (!pickerKey || siteScopePickerUiState.openKey !== pickerKey) {
            return;
        }

        if (isActiveElementWithinSiteScopePicker(pickerKey)) {
            return;
        }

        closeSiteScopePicker(pickerKey);
    }, 0);
}

// ── Portal event listeners ─────────────────────────────────────────────────────

siteScopePickerPortalHost.addEventListener('click', (event) => {
    handleSiteScopePickerClick(event);
});

siteScopePickerPortalHost.addEventListener('keydown', (event) => {
    handleSiteScopePickerKeydown(event);
});

siteScopePickerPortalHost.addEventListener('input', (event) => {
    handleSiteScopePickerInput(event);
});

siteScopePickerPortalHost.addEventListener('focusout', (event) => {
    handleSiteScopePickerFocusOut(event);
});
